import type {ComponentEnhancer} from 'recompose'
import {withHandlers, branch as branchHOC} from 'recompose'

import compose from 'lodash/flowRight'
import * as React from 'react'
import {connect} from 'react-redux'

import {resetBuildsLocatorState} from '../actions'
import {fetchBuildsData} from '../actions/builds'
import type {FetchBuildDataParams} from '../actions/builds'
import Fetcher from '../components/common/Fetcher/Fetcher'
import {usePage} from '../components/common/Pager/Pager.hooks'
import {memoWithDisplayName} from '../hocs/memoWithDisplayName'
import withEffect from '../hocs/withEffect'
import withHook from '../hocs/withHook'
import {useBooleanQueryParam, useQueryParams, withProjectOrBuildTypeNode} from '../hooks/routes'
import type {State} from '../reducers/types'
import {getLocator} from '../rest/locators'
import {getBuildType} from '../selectors'
import {getProjectBuildTypeFilter, toBuildTypeId, toProjectId} from '../types'
import type {
  BuildTypeInternalId,
  LocatorOptions,
  ProjectOrBuildTypeNode,
  ActiveEntityProps,
  BuildId,
} from '../types'
import {noop} from '../utils/empty'
import {notNull} from '../utils/guards'
import type {Refetchable} from '../utils/refetchable'
import createSimpleStream from '../utils/simpleStream'
import type {SimpleStream} from '../utils/simpleStream'
import {subscribeOnBuildTypeEvents} from '../utils/subscriber'
import * as SubscriptionEvents from '../utils/subscriptionEvents'

type OwnProps = LocatorOptions &
  ActiveEntityProps & {
    locator?: string | null | undefined
    withPager?: boolean
    withQueued?: boolean
    withRunning?: boolean
    withBuildTypeDetails?: boolean
    withPipelineRuns?: boolean
    withAnchorBuild?: boolean
    withSnapshotDependencies?: boolean
    withDownloadedArtifactsFrom?: BuildId | null
    withProgress?: boolean
    withBuildDetails?: boolean
    withShortBuildDetails?: boolean
    fetchCount?: number
    projectOrBuildTypeNode?: ProjectOrBuildTypeNode | null | undefined
    update$?: SimpleStream<void>
    updatePeriod?: number | null | undefined
    children?: React.ReactNode
    pause?: boolean
    customEndpoint?: string
  }
type StateProps = {
  buildTypeInternalId: BuildTypeInternalId | null | undefined
}
type ActionProps = {
  fetchBuilds: (arg0: FetchBuildDataParams) => Refetchable
  resetLocatorState?: (locator: string) => unknown
}
type AddProps = {
  page: number
  getUpdate$: () => SimpleStream<void>
}

const mapStateToProps = (state: State, {buildTypeId}: OwnProps): StateProps => ({
  buildTypeInternalId: getBuildType(state, buildTypeId)?.internalId,
})

const actionCreators = {
  fetchBuilds: fetchBuildsData,
  resetLocatorState: resetBuildsLocatorState,
}

type Props = OwnProps & StateProps & ActionProps & AddProps

class BuildsFetcher extends React.Component<Props> {
  static defaultProps = {
    withQueued: false,
    withRunning: false,
    withBuildTypeDetails: true,
    withSnapshotDependencies: false,
  }

  unsubscribeCache = noop

  componentWillUnmount() {
    this.unsubscribeCache()
  }

  fetchData = (
    locator: string,
    options: {inBackground?: boolean; forceRefetch?: boolean},
    page = 1,
  ) => {
    const {
      fetchBuilds,
      withPager,
      withRunning,
      withQueued,
      withBuildTypeDetails,
      withSnapshotDependencies,
      withDownloadedArtifactsFrom,
      withProgress,
      withBuildDetails,
      withPipelineRuns,
      withAnchorBuild,
      fetchCount,
      customEndpoint,
    } = this.props
    this.unsubscribeCache()
    const withPipelineDetails = withPipelineRuns || withAnchorBuild
    const result = fetchBuilds({
      locator,
      withPager,
      page,
      requestOptions: {
        fetchCount,
        withProgress,
        withBuildTypeDetails,
        withSnapshotDependencies,
        withDownloadedArtifactsFrom,
        withQueuedInfo: !withPipelineDetails ? withQueued : false,
        withRunningInfo: !withPipelineDetails ? withRunning : false,
        withBuildDetails,
        customEndpoint,
      },
      ...(withPipelineDetails
        ? {
            extraRequestOptions: {
              withShortBuildDetails: true,
              withPipelineRuns,
              withAnchorBuild,
              withRunningInfo: withRunning,
              withQueuedInfo: withQueued,
              customEndpoint,
            },
          }
        : {}),
      inBackground: options.inBackground,
      forceRefetch: options.forceRefetch,
    })
    this.unsubscribeCache = result.unsubscribe
    return result
  }

  render() {
    const {
      buildTypeInternalId,
      getUpdate$,
      updatePeriod,
      locator,
      page,
      resetLocatorState,
      children,
      pause,
    } = this.props

    return (
      <Fetcher
        locator={locator}
        page={page}
        resetLocatorState={resetLocatorState}
        pause={pause}
        fetchData={this.fetchData}
        update$={getUpdate$()}
        updatePeriod={buildTypeInternalId != null ? null : updatePeriod}
        hasSubscription={buildTypeInternalId != null}
      >
        {children}
      </Fetcher>
    )
  }
}

export const getEvents = (
  withQueued: boolean | null | undefined,
  withRunning: boolean | null | undefined,
): ReadonlyArray<string> =>
  [
    withQueued === true ? SubscriptionEvents.BUILD_TYPE_ADDED_TO_QUEUE : null,
    withQueued === true ? SubscriptionEvents.BUILD_TYPE_REMOVED_FROM_QUEUE : null,
    withRunning === true || withQueued === true ? SubscriptionEvents.BUILD_STARTED : null,
    withRunning === true ? SubscriptionEvents.BUILD_CHANGES_LOADED : null,
    SubscriptionEvents.BUILD_FINISHED,
    SubscriptionEvents.BUILD_INTERRUPTED,
    SubscriptionEvents.BUILD_REMOVED,
    SubscriptionEvents.BUILD_TAGS_CHANGED,
  ].filter(notNull)

const BuildsFetcherContainer: React.ComponentType<OwnProps> = compose(
  memoWithDisplayName,
  branchHOC(
    ({projectId, buildTypeId, projectOrBuildTypeNode}: OwnProps) =>
      !projectOrBuildTypeNode && !projectId && !buildTypeId,
    withProjectOrBuildTypeNode as ComponentEnhancer<OwnProps, OwnProps>,
  ),
  withHook((props: OwnProps) => {
    const {
      state,
      tag,
      locator,
      buildTypeId: buildTypeIdString,
      projectId,
      'agent-name-pattern': agentPattern,
    } = useQueryParams()
    const buildTypeId = buildTypeIdString != null ? toBuildTypeId(buildTypeIdString) : undefined
    const page = usePage()
    const useRawLocator = useBooleanQueryParam('use-raw-locator')

    return {
      ...props,
      locator:
        props.locator ??
        getLocator({
          ...props,
          projectBuildtype:
            props.projectOrBuildTypeNode ??
            getProjectBuildTypeFilter({
              projectId: projectId != null ? toProjectId(projectId) : null,
              buildTypeId,
            }) ??
            undefined,
          withRunningAndQueued: props.withRunning === true || props.withQueued,
          buildState: props.buildState ?? state ?? undefined,
          tag,
          agentPattern,
          useRawLocator,
          rawLocator: locator,
        }),
      page: Number(page),
    }
  }),
  connect(mapStateToProps, actionCreators),
  memoWithDisplayName,
  withHandlers(() => {
    const update$ = createSimpleStream()
    return {
      getUpdate$: (props: OwnProps & StateProps & ActionProps) => () => props.update$ ?? update$,
    }
  }),
  withEffect(
    (props: Props) => props.buildTypeInternalId,
    props => props.withQueued,
    props => props.withRunning,
    props => props.getUpdate$,
    props => props.pause,
    props => props.locator,
    (internalId, withQueued, withRunning, getUpdate$, pause) =>
      internalId != null && !pause
        ? subscribeOnBuildTypeEvents(internalId, getEvents(withQueued, withRunning), () =>
            getUpdate$().fire(),
          )
        : undefined,
  ),
)(BuildsFetcher)
export default BuildsFetcherContainer
